export type ContactStatus = "to_contact" | "contacted" | "connected" | "not_relevant";

export interface Contact {
  id: string;
  email: string;
  name: string;
  source: "waitlist" | "twitter" | "linkedin" | "manual";
  status: ContactStatus;
  purpose: string;
  notes: string;
  dateAdded: string; // ISO date
  platform: string; // twitter handle, linkedin url, etc.
}

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  to_contact: "To Contact",
  contacted: "Contacted",
  connected: "Connected",
  not_relevant: "Not Relevant",
};

export const CONTACT_STATUS_VARIANTS: Record<ContactStatus, "warning" | "info" | "success" | "default"> = {
  to_contact: "warning",
  contacted: "info",
  connected: "success",
  not_relevant: "default",
};

// Internal / team emails to exclude
const INTERNAL_EMAILS = new Set([
  "albertobelleiro@gmail.com",
  "contact@ai-wave.co",
  "aiwaveobe@gmail.com",
  "joelaiwave@gmail.com",
  "albert@ai-wave.co",
  "joel@ai-wave.co",
  "joelrodrim03@gmail.com",
]);

interface RawCsvRow {
  date: string;
  email: string;
}

function parseWaitlistCsv(): RawCsvRow[] {
  // Pre-parsed from the CSV — we inline the data to avoid runtime file reads
  const rawRows: RawCsvRow[] = [
    { date: "2025-03-28", email: "joelrodrim03@gmail.com" },
    { date: "2025-03-29", email: "albertobelleiro@gmail.com" },
    { date: "2025-04-01", email: "jude.whitford@gmail.com" },
    { date: "2025-04-01", email: "contact@ai-wave.co" },
    { date: "2025-04-01", email: "aiwaveobe@gmail.com" },
    { date: "2025-04-01", email: "joelaiwave@gmail.com" },
    { date: "2025-04-01", email: "albertobelleiro@gmail.com" },
    { date: "2025-04-01", email: "albert@ai-wave.co" },
    { date: "2025-04-02", email: "ablanquicet@agapai.com.co" },
    { date: "2025-04-02", email: "oluwatobilobagunloye@gmail.com" },
    { date: "2025-04-02", email: "lovro@zitnik.com" },
    { date: "2025-04-02", email: "michael@midasdx.com" },
    { date: "2025-04-03", email: "mario.orba96@gmail.com" },
    { date: "2025-04-03", email: "ckodsi@sas.upenn.edu" },
    { date: "2025-04-04", email: "loai.123.sh@gmail.com" },
    { date: "2025-04-18", email: "trevor@withyotta.com" },
    { date: "2025-04-22", email: "lizcraig.digital@gmail.com" },
    { date: "2025-04-23", email: "jari.pauna@gmail.com" },
    { date: "2025-04-25", email: "demoacc975@gmail.com" },
    { date: "2025-06-16", email: "bachir@bidew.io" },
    { date: "2025-06-20", email: "joel@ai-wave.co" },
    { date: "2025-06-22", email: "iworkfor707@yahoo.com" },
    { date: "2025-06-22", email: "aboveandbeyondcannabis@yahoo.com" },
    { date: "2025-06-22", email: "sydneyecommerce@gmail.com" },
    { date: "2025-06-23", email: "hikaran.malhotra@gmail.com" },
    { date: "2025-06-24", email: "Mike@standupworld.com" },
    { date: "2025-06-25", email: "rodriguezx@icloud.com" },
    { date: "2025-06-25", email: "govimij249@coasah.com" },
    { date: "2025-06-30", email: "brianmrose@outlook.com" },
    { date: "2025-07-01", email: "pauregas@gmail.com" },
    { date: "2025-07-02", email: "santiago@tuconsumo.es" },
    { date: "2025-07-07", email: "team@collabsy.io" },
    { date: "2025-07-08", email: "xlpaskidda@gmail.com" },
    { date: "2025-07-09", email: "twyentertainment305@gmail.com" },
    { date: "2025-07-10", email: "ralph@aionicdigital.com" },
    { date: "2025-07-10", email: "chakmal342@gmail.com" },
    { date: "2025-07-10", email: "zahurul401@gmail.com" },
    { date: "2025-07-12", email: "tarynb51@gmail.com" },
    { date: "2025-07-12", email: "avnikita200347@gmail.com" },
    { date: "2025-07-15", email: "mikechilewejnr@gmail.com" },
    { date: "2025-07-19", email: "team@micepadapp.com" },
    { date: "2025-07-24", email: "carlosbalaguer5@gmail.com" },
    { date: "2025-07-31", email: "jehovan@gmail.com" },
    { date: "2025-08-07", email: "excelantoine@gmail.com" },
    { date: "2025-08-08", email: "giorgipipia17@gmail.com" },
    { date: "2025-08-21", email: "rajacohir212@gmail.com" },
    { date: "2025-09-08", email: "craigsb93@gmail.com" },
    { date: "2025-09-08", email: "os1ch420@gmail.com" },
    { date: "2025-09-08", email: "abigailholland22@gmail.com" },
    { date: "2025-09-10", email: "chappellpi@gmail.com" },
    { date: "2025-09-17", email: "craigsb93@gmail.com" },
    { date: "2025-10-07", email: "lanamab@gmail.com" },
    { date: "2025-10-23", email: "romatchin@gmail.com" },
    { date: "2025-12-03", email: "milichtest@gmail.com" },
    { date: "2025-12-14", email: "1814285860@qq.com" },
    { date: "2025-12-23", email: "owenxcountry@gmail.com" },
    { date: "2026-02-04", email: "neeraj@wesoftek.com" },
  ];

  return rawRows;
}

function emailToName(email: string): string {
  const local = email.split("@")[0] ?? email;
  // Try to extract a human name from the local part
  const cleaned = local
    .replace(/[._]/g, " ")
    .replace(/\d+/g, "")
    .trim();
  if (!cleaned || cleaned.length < 2) return email;
  return cleaned
    .split(" ")
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function emailToPlatformHint(email: string): string {
  const domain = email.split("@")[1] ?? "";
  if (domain.includes("gmail") || domain.includes("yahoo") || domain.includes("outlook") || domain.includes("icloud") || domain.includes("qq.com")) {
    return "";
  }
  // Business email — extract company name
  const company = domain.split(".")[0] ?? "";
  return company.charAt(0).toUpperCase() + company.slice(1);
}

// ── Outreach contacts (Twitter / LinkedIn / Manual) ──────────
const OUTREACH_CONTACTS: Array<{
  name: string;
  source: Contact["source"];
  purpose: string;
  notes: string;
}> = [
  {
    name: "David",
    source: "linkedin",
    purpose: "CTO & CIO of Adtlanter — tech leadership contact",
    notes: "CTO and CIO of Adtlanter",
  },
  {
    name: "Alberto",
    source: "linkedin",
    purpose: "Sales expertise — can advise on GTM sales strategy",
    notes: "Very experienced seller",
  },
  {
    name: "Eve",
    source: "linkedin",
    purpose: "",
    notes: "",
  },
  {
    name: "Contacto de mi padre",
    source: "manual",
    purpose: "Warm intro — Razzmataz network",
    notes: "Contact from Razzmataz",
  },
  {
    name: "Factorial",
    source: "linkedin",
    purpose: "CEOs of Factorial — high-value B2B SaaS connection",
    notes: "CEOs of Factorial. Intro via David",
  },
  {
    name: "Esteban",
    source: "linkedin",
    purpose: "Sales expertise — experienced seller",
    notes: "Experienced seller",
  },
  {
    name: "Bids",
    source: "linkedin",
    purpose: "Large contact list & potential client",
    notes: "Large contact list and potential client",
  },
  {
    name: "Axxa",
    source: "linkedin",
    purpose: "Banking/finance sector — branch manager connection",
    notes: "Axxa branch manager",
  },
  {
    name: "Auxiliares",
    source: "linkedin",
    purpose: "",
    notes: "",
  },
  {
    name: "Henry",
    source: "linkedin",
    purpose: "",
    notes: "",
  },
  {
    name: "Vamos a comer",
    source: "manual",
    purpose: "",
    notes: "",
  },
  {
    name: "Áticco",
    source: "manual",
    purpose: "Coworking network — all contacts made at Áticco",
    notes: "All the contacts we made in Áticco coworking",
  },
  {
    name: "Novoa",
    source: "linkedin",
    purpose: "Large contact list & real estate company",
    notes: "Large contact list and real estate company",
  },
  {
    name: "Texido",
    source: "linkedin",
    purpose: "CEO of Pasiona — large Barcelona tech company",
    notes: "CEO of Pasiona, a large tech company from Barcelona",
  },
  {
    name: "Marc Áticco",
    source: "manual",
    purpose: "Áticco coworking seller — local network",
    notes: "Áticco coworking seller",
  },
  {
    name: "Atipika",
    source: "linkedin",
    purpose: "Founding CEO of Atipika — large Barcelona real estate company",
    notes: "Founding CEO of Atipika, a large real estate company in Barcelona",
  },
  {
    name: "Zem",
    source: "manual",
    purpose: "Contact list — runs a taco company",
    notes: "Contact list, runs a taco company",
  },
  {
    name: "Claudio",
    source: "manual",
    purpose: "Director of beach bars, parties & security — Castelldefels/Barcelona",
    notes: "Director of several beach bars, parties and security in Castelldefels/Barcelona",
  },
  {
    name: "Lancaster",
    source: "manual",
    purpose: "Lancaster Castelldefels group",
    notes: "Lancaster Castelldefels group",
  },
  {
    name: "Tibu",
    source: "manual",
    purpose: "Castelldefels shark group",
    notes: "Castelldefels shark group",
  },
  {
    name: "X / Twitter & LinkedIn",
    source: "twitter",
    purpose: "Contacts made through X/Twitter and LinkedIn",
    notes: "Contacts we have made through X/Twitter and LinkedIn",
  },
  {
    name: "Alesso",
    source: "linkedin",
    purpose: "Huge client portfolio — 5000 clients",
    notes: "List of his clients (5000 clients in the client portfolio)",
  },
  {
    name: "Luis Fernández Bauerbaum",
    source: "linkedin",
    purpose: "Great list of contacts",
    notes: "Great list of contacts",
  },
];

export function buildInitialContacts(): Contact[] {
  const rows = parseWaitlistCsv();
  const seen = new Set<string>();
  const contacts: Contact[] = [];

  // ── 1. Outreach contacts first (higher priority) ─────────
  for (const entry of OUTREACH_CONTACTS) {
    contacts.push({
      id: `out-${contacts.length + 1}`,
      email: "",
      name: entry.name,
      source: entry.source,
      status: "to_contact",
      purpose: entry.purpose,
      notes: entry.notes,
      dateAdded: "2026-02-27",
      platform: "",
    });
  }

  // ── 2. Waitlist contacts ──────────────────────────────────
  for (const row of rows) {
    const email = row.email.trim().toLowerCase();
    if (!email || email.length < 3) continue;
    if (INTERNAL_EMAILS.has(email)) continue;
    if (seen.has(email)) continue;
    seen.add(email);

    const company = emailToPlatformHint(email);

    contacts.push({
      id: `wl-${contacts.length + 1}`,
      email,
      name: emailToName(email),
      source: "waitlist",
      status: "to_contact",
      purpose: "",
      notes: company ? `Company: ${company}` : "",
      dateAdded: row.date,
      platform: "",
    });
  }

  return contacts;
}

// Singleton so we get a consistent initial load
let _cache: Contact[] | null = null;
export function getInitialContacts(): Contact[] {
  if (!_cache) _cache = buildInitialContacts();
  return _cache;
}
